import { useCallback, useEffect, useState } from "react";
import { TrashIcon } from "@heroicons/react/24/outline";
import { supabase } from "../../lib/supabase";
import { useAuthStore } from "../../stores/auth";
import {
  AdminTable,
  type AdminTableColumn,
  ConfirmModal,
  Pagination,
  PageHeader,
  SearchBar,
  useDebouncedValue,
} from "../../components/admin/adminPrimitives";

interface AllowlistEntry {
  email: string;
  note: string | null;
  added_by: string | null;
  created_at: string;
}

const PAGE_SIZE = 25;

export function SignupAllowlist() {
  const { user } = useAuthStore();
  const [entries, setEntries] = useState<AllowlistEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sendingTo, setSendingTo] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [pendingDelete, setPendingDelete] = useState<AllowlistEntry | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError(null);

    const from = page * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    let query = supabase
      .from("signup_allowlist")
      .select("email, note, added_by, created_at", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(from, to);

    const q = debouncedSearch.trim().toLowerCase();
    if (q) {
      const safe = q.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
      query = query.or(`email.ilike.%${safe}%,note.ilike.%${safe}%`);
    }

    const { data, count, error: fetchError } = await query;
    if (fetchError) {
      console.error("Failed to fetch allowlist:", fetchError);
      setError("Failed to load allowlist: " + fetchError.message);
      setEntries([]);
      setTotal(0);
    } else {
      setEntries((data ?? []) as AllowlistEntry[]);
      setTotal(count ?? 0);
    }
    setLoading(false);
  }, [page, debouncedSearch]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);

    const normalized = email.trim().toLowerCase();
    if (!normalized) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) {
      setError("Please enter a valid email address.");
      return;
    }

    setSubmitting(true);
    try {
      const { error: insertError } = await supabase
        .from("signup_allowlist")
        .insert({
          email: normalized,
          note: note.trim() || null,
          added_by: user?.id ?? null,
        });

      if (insertError) {
        if (insertError.code === "23505") {
          setError(`${normalized} is already on the allowlist.`);
        } else {
          setError("Failed to add entry: " + insertError.message);
        }
        return;
      }

      setEmail("");
      setNote("");
      await fetchEntries();
      await sendInvite(normalized);
    } catch {
      setError("Could not confirm the allowlist update. Refresh the list before trying again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function sendInvite(address: string) {
    setSendingTo(address);
    setNotice(null);
    setError(null);
    try {
      const { data, error: sendError } = await supabase.functions.invoke("send-signup-invite", {
        body: { email: address },
      });
      if (sendError || !data?.sent) {
        setError(data?.error || `${address} is on the allowlist, but the invitation could not be confirmed. Use Send invite to retry.`);
      } else {
        setNotice(`Invitation accepted for sending to ${address}. Use the newest email; requesting another invitation can replace the previous link.`);
      }
    } catch {
      setError( `${address} is on the allowlist, but the invitation could not be confirmed. Use Send invite to retry.`);
    } finally {
      setSendingTo(null);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    const { error: delError } = await supabase
      .from("signup_allowlist")
      .delete()
      .eq("email", pendingDelete.email);
    setDeleting(false);

    if (delError) {
      setError("Failed to remove entry: " + delError.message);
      setPendingDelete(null);
      return;
    }
    setPendingDelete(null);
    await fetchEntries();
  }

  const columns: AdminTableColumn<AllowlistEntry>[] = [
    {
      key: "email",
      header: "Email",
      render: (entry) => (
        <span className="font-mono text-xs text-forest-900">{entry.email}</span>
      ),
    },
    {
      key: "note",
      header: "Note",
      render: (entry) => (
        <span className="text-forest-600">{entry.note ?? "\u2014"}</span>
      ),
    },
    {
      key: "added",
      header: "Added",
      render: (entry) => (
        <span className="text-forest-500">
          {new Date(entry.created_at).toLocaleDateString("en-GB")}
        </span>
      ),
    },
    {
      key: "action",
      header: "",
      align: "right",
      render: (entry) => (
        <div className="flex items-center justify-end gap-2">
        <button
          onClick={() => sendInvite(entry.email)}
          disabled={sendingTo !== null || submitting || deleting}
          className="rounded-lg px-2 py-1 text-xs text-forest-700 hover:bg-forest-50 disabled:opacity-50"
          aria-label={`Send invite to ${entry.email}`}
        >
          {sendingTo === entry.email ? "Sending..." : "Send invite"}
        </button>
        <button
          onClick={() => setPendingDelete(entry)}
          disabled={sendingTo !== null || submitting || deleting}
          className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-red-600 hover:bg-red-50"
          aria-label={`Remove ${entry.email}`}
        >
          <TrashIcon className="h-4 w-4" />
          Remove
        </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Signup Allowlist"
        description="Adding an email sends an invitation to create a Bookburrow account and start a free trial. Only emails on this list can sign up. Removing an entry does not revoke an existing account."
        actions={
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search email or note"
          />
        }
      />

      <form onSubmit={handleAdd} className="mb-6 max-w-2xl">
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="teacher@school.sch.uk"
            className="paper-card flex-1 rounded-lg border border-forest-200 bg-white px-4 py-2 text-sm text-forest-900 placeholder:text-forest-400 focus:border-forest-500 focus:outline-none"
            required
          />
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note (optional)"
            className="paper-card flex-1 rounded-lg border border-forest-200 bg-white px-4 py-2 text-sm text-forest-900 placeholder:text-forest-400 focus:border-forest-500 focus:outline-none"
          />
          <button
            type="submit"
            disabled={submitting || sendingTo !== null || !email.trim()}
            className="rounded-lg bg-forest-900 px-4 py-2 text-sm font-medium text-cream-100 hover:bg-forest-800 disabled:opacity-50"
          >
            {submitting ? "Adding and inviting..." : "Add and invite"}
          </button>
        </div>
      </form>

      {error && <p role="alert" className="mb-3 text-sm text-red-600">{error}</p>}
      {notice && <p role="status" className="mb-3 text-sm text-forest-700">{notice}</p>}

      <AdminTable
        columns={columns}
        rows={entries}
        loading={loading}
        rowKey={(e) => e.email}
        emptyMessage={
          debouncedSearch
            ? `No allowlist entries match "${debouncedSearch}".`
            : "No emails on the allowlist yet. Add one above to let someone sign up."
        }
      />

      <Pagination
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
      />

      <ConfirmModal
        open={pendingDelete !== null}
        title="Remove from allowlist?"
        description={
          pendingDelete && (
            <>
              The email{" "}
              <span className="font-mono text-forest-900">
                {pendingDelete.email}
              </span>{" "}
              will no longer be able to create an account. Existing accounts
              are unaffected.
            </>
          )
        }
        confirmLabel="Remove"
        confirmTone="red"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
        busy={deleting}
      />
    </div>
  );
}
